import type { IDraftListing } from './draft-listing.types';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

/**
 * Per-listing snapshot persisted at publish time so `/listing?id=N` can
 * hydrate the host's real data (photos, host name, rules) instead of falling
 * through to the mock generators in `getListingDetail`.
 *
 * Backed by IndexedDB (~50 MB+ quota, designed for blob-ish data like the
 * photo data URLs we keep here) with a synchronous in-memory cache so the
 * existing consumers (`getListingDetail`, `hydratePublishedListings`) don't
 * need to become async. Built on the shared `createIdbKvStore` abstraction
 * this pattern was later generalized into (see `idb-kv-store.util.ts`) —
 * this was the original hand-rolled reference implementation, folded onto
 * the shared one for consistency, still on its own database (unrelated to
 * the shared `cnt-landing-kv` store the rest of the app's ~30 other domains
 * use) so this never shares a version/upgrade path with them.
 *
 * Bootstrap order:
 *   - `initPublishedSnapshots()` runs once via APP_INITIALIZER and populates
 *     the cache from IDB (migrating from the legacy localStorage key on
 *     first run).
 *   - Reads (`readPublishedSnapshot`, `readAllPublishedSnapshots`) are
 *     synchronous and hit the cache.
 *   - Writes (`writePublishedSnapshot`) update the cache immediately and
 *     return a Promise that resolves once the IDB write lands — callers can
 *     `await` it (for publish flows where we want to know it stuck) or
 *     fire-and-forget (for autosave).
 */
export const PUBLISHED_SNAPSHOTS_KEY = 'cnt-published-snapshots';
const DB_NAME = 'cnt-landing';
const STORE = 'published-snapshots';
const DB_VERSION = 1;

export interface IPublishedSnapshot {
  draft: IDraftListing;
  hostName: string;
  hostInitials: string;
  hostAvatar: string;
  hostJoinedYear: number;
  hostEmail: string;
  /** Real backend `ISite.id` for this listing's (single, host-wizard-created)
   *  site — needed so edits can target the right site's content-update
   *  endpoints. Undefined for listings that predate the real-backend wiring. */
  realSiteId?: number;
}

// No `legacyKeys` config here — the legacy localStorage value is a single
// JSON blob mapping many listing ids to snapshots (`{ "12": {...}, "34":
// {...} }`), not a 1:1 idbKey->legacyKey pair the generic config supports.
// initPublishedSnapshots() below expands it manually, once.
const snapshotStore = createIdbKvStore<IPublishedSnapshot>({
  dbName: DB_NAME,
  storeName: STORE,
  version: DB_VERSION,
  legacyKeys: {},
});

/**
 * Idempotent boot loader. Migrates the legacy `cnt-published-snapshots`
 * localStorage map into IDB on first run, then drops the legacy key. Safe
 * to call multiple times — only the first call does work.
 */
export async function initPublishedSnapshots(): Promise<void> {
  await snapshotStore.init();
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(PUBLISHED_SNAPSHOTS_KEY);
    if (raw) {
      const map = JSON.parse(raw) as Record<string, IPublishedSnapshot>;
      for (const [k, v] of Object.entries(map)) {
        await snapshotStore.write(k, v);
      }
      localStorage.removeItem(PUBLISHED_SNAPSHOTS_KEY);
    }
  } catch {
    // Legacy parse failure — skip migration; IDB load (via init() above) still wins.
  }
}

/** Synchronous; returns null when no snapshot exists for the id. */
export function readPublishedSnapshot(listingId: number): IPublishedSnapshot | null {
  return snapshotStore.read(String(listingId)) ?? null;
}

/** Synchronous; returns a fresh plain object so callers can mutate freely. */
export function readAllPublishedSnapshots(): Record<string, IPublishedSnapshot> {
  const out: Record<string, IPublishedSnapshot> = {};
  for (const [k, v] of snapshotStore.readAll()) out[k] = v;
  return out;
}

/**
 * Persist a snapshot. Updates the synchronous cache immediately and queues
 * the IDB write. The returned promise resolves once IDB acknowledges the
 * write; reject path means storage is full or unavailable.
 */
export async function writePublishedSnapshot(listingId: number, snapshot: IPublishedSnapshot): Promise<void> {
  await snapshotStore.write(String(listingId), snapshot);
}

/**
 * Permanently remove a listing's snapshot. Drops the in-memory cache entry
 * immediately and queues the IDB delete. Used by the host-side delete-listing
 * flow alongside refreshing the real ownership cache (`HostListingsService`).
 */
export async function deletePublishedSnapshot(listingId: number): Promise<void> {
  await snapshotStore.delete(String(listingId));
}

/** Drops every published-listing snapshot on the device — called on sign-out
 *  (see `AuthService.loggedOut$` and its subscriber in
 *  `host-listing-draft.service.ts`). Each snapshot carries the host's own
 *  name/initials/avatar/email, so these count as personal data even though
 *  the underlying listing itself lives on the real backend. */
export async function clearAllPublishedSnapshots(): Promise<void> {
  await Promise.all(Object.keys(readAllPublishedSnapshots()).map(id => snapshotStore.delete(id)));
}

/** Test-only: resets the module-level cache and init state. Production code
 *  never calls this — mirrors `draft-store.util.ts`'s
 *  `__resetDraftStoreForTesting()`. */
export function __resetPublishedSnapshotsForTesting(): void {
  snapshotStore.__resetForTesting();
}
